import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import Chip from '@mui/material/Chip'
import { useEffect, useState } from 'react'
import { t } from 'ttag'

export type OptionType = [string, string]

interface SelectProps {
  handleChange: (selected: string[]) => void
  options: OptionType[]
  initialValues?: OptionType[]
  label: string
  required?: boolean
  disabled?: boolean
  removeDuplicates?: boolean
  isLoading?: boolean
}

export default function MultiSelect(props: Readonly<SelectProps>) {
  const {
    handleChange,
    label,
    options,
    initialValues = [],
    required = false,
    disabled = false,
    removeDuplicates = true,
    isLoading = false
  } = props

  const [selectedValue, setSelectedValue] = useState<OptionType[]>(initialValues)
  useEffect(() => {
    if (initialValues.length > 0) {
      setSelectedValue(initialValues)
    }
  }, [initialValues])
  const handleSelectChange = (_event: React.SyntheticEvent, value: Array<OptionType> | null) => {
    if (value) {
      // if empty value is selected, clear selected options
      if (value.some(item => item[0] === '')) {
        setSelectedValue([])
        handleChange([])
        return
      }
      setSelectedValue(value)
      handleChange(value.map(option => option[0]))
    }
  }

  // At least in dev environment's data there are duplicates, so here we remove them (if removeDuplicates is true)
  const filteredOptions = removeDuplicates
    ? options.filter((option, index, array) => array.findIndex(t => t[1] == option[1]) == index)
    : options

  const getSelectedOptionLabel = (option: OptionType) => {
    const selectedOption = filteredOptions.find(obj => {
      return obj[0] === option[0]
    })
    if (selectedOption) {
      return selectedOption[1]
    } else {
      return t`Option not found` + `: ${option[0]}`
    }
  }

  return (
    <Autocomplete
      multiple
      options={filteredOptions}
      value={selectedValue}
      getOptionLabel={option => option[1]}
      isOptionEqualToValue={(option, value) => option[0] == value[0]}
      filterSelectedOptions
      disableCloseOnSelect
      loading={isLoading}
      disabled={disabled || isLoading}
      onChange={handleSelectChange}
      renderTags={(value: OptionType[], getTagProps) =>
        isLoading ? (
          <CircularProgress size={20} sx={{ ml: 1, mr: 1 }} />
        ) : (
          value.map((option: OptionType, index) => (
            <Chip
              variant="outlined"
              label={getSelectedOptionLabel(option)}
              {...getTagProps({ index })}
              key={option[0]}
            />
          ))
        )
      }
      renderInput={params => (
        <TextField
          sx={{ backgroundColor: 'background.default' }}
          {...params}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              required: required && selectedValue.length === 0 // Autocomplete component does not provide required attr so this is a workaround for checking whether the autocomplete is empty
            }
          }}
          required={required}
          label={label}
        />
      )}
    />
  )
}
